import { useMutation, useQueryClient } from '@tanstack/react-query';
import { applyTagUpdate } from 'src/features/models/queries/utils/tagMutationHelpers';
import { useToast } from 'src/shared/components/ui/use-toast';
import { useOrg } from 'src/shared/contexts/OrgContext';
import mixpanel from 'src/shared/lib/mixpanel';
import { genQueryKey } from 'src/shared/lib/query.utils';
import { Model, Tag } from 'src/shared/types/model.types';

export type NewTagProps = {
  id?: string;
  shortName: string;
  internalId: string;
  description?: string;
  parentId?: string;
  isGrouped?: boolean;
  modelId?: string;
};

type TagMutationProps = {
  tagId: string;
  patch: {
    shortName?: string;
    description?: string;
    parentId?: string | null;
    extra?: Record<string, any>;
    nerExamples?: string[];
    feedback?: Tag['feedback'];
    isGrouped?: boolean;
    rules?: Tag['rules'];
    type?: 'auto' | 'manual' | 'imported' | 'sink';
    status?: 'ACTIVE' | 'REMOVED' | 'PENDING_ADDED' | 'PENDING_REMOVED' | 'PENDING_UPDATED' | 'SUGGESTED';
  };
};

type ExportTagProps = {
  model: Model;
  integrations: string[];
  instructions?: string;
  timeframe: number;
  seed?: {
    name: string;
    description: string;
    id: string;
  }[];
  deepdive?: {
    name: string;
    parentId: string;
  };
};

type UseMutateTagOptions = {
  onSuccess?: (tag: Tag) => void | Promise<void>;
};

export function useMutateTag(modelId: string, options?: UseMutateTagOptions) {
  const queryClient = useQueryClient();
  const { orgFetch, currentOrg, role } = useOrg();
  const { errorToast } = useToast();

  const queryKey = ['models', genQueryKey(currentOrg.id)];

  const newTag = useMutation({
    mutationFn: async ({
      internalId,
      description,
      shortName,
      parentId = '',
      isGrouped,
      modelId: modelIdOverwrite,
    }: NewTagProps) => {
      if (['READER', 'SALES'].includes(role)) {
        throw new Error('Users with Reader or Sales role cannot create tags');
      }
      const safeInternalId = (internalId || shortName).replace(/\s+/g, '_');
      const res = await orgFetch<Tag>(`/api/v2/models/${modelIdOverwrite || modelId}/tags`, {
        method: 'POST',
        body: JSON.stringify({
          internalId: safeInternalId,
          shortName,
          description,
          parentId: parentId.length > 0 ? parentId : undefined,
          isGrouped,
          type: 'manual',
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(`Unable to create tag: ${JSON.stringify(data)}`);
      }
      return data;
    },
    onSuccess: async (tag: Tag) => {
      // Always refetch the models query
      await queryClient.refetchQueries({ queryKey, type: 'active' });
      console.log('[useMutateTag.newTag] Refetch completed');

      // Track analytics
      mixpanel.track('Tag Added', {
        source: 'Manual',
      });

      // Call custom onSuccess if provided (allows components to override default behavior)
      if (options?.onSuccess) {
        await options.onSuccess(tag);
      }
    },
    onError: (error) => {
      console.log(error);
      errorToast('Failed to create tag');
    },
  });

  const deleteTag = useMutation({
    mutationFn: async (tagId: string) => {
      if (['READER', 'SALES'].includes(role)) {
        return;
      }
      const res = await orgFetch(`/api/v2/models/${modelId}/tags/${tagId}`, {
        method: 'DELETE',
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(`Unable to delete tag: ${JSON.stringify(data)}`);
      }
      return data;
    },
    onSuccess: async () => {
      await queryClient.cancelQueries({ queryKey });
      queryClient.refetchQueries({ queryKey });
    },
    onError: (error) => {
      console.log(error);
      errorToast('Failed to delete tag');
    },
  });

  const updateTag = useMutation({
    mutationFn: async ({ tagId, patch }: TagMutationProps) => {
      if (['READER', 'SALES'].includes(role)) {
        return;
      }
      const res = await orgFetch<Tag>(`/api/v2/models/${modelId}/tags/${tagId}`, {
        method: 'PATCH',
        body: JSON.stringify(patch),
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(`Tag update failed: ${JSON.stringify(data)}`);
      }
    },

    onMutate: async ({ tagId, patch }: TagMutationProps) => {
      await queryClient.cancelQueries({ queryKey });
      const previousData = queryClient.getQueryData<Model[]>(queryKey);

      let updatedData: Model[] = [];
      if (previousData) {
        updatedData = previousData.map((model) => {
          const tagExists = model.tags.some((tag) => tag.id === tagId);
          if (!tagExists) {
            return model;
          }

          const updatedTags = applyTagUpdate(model.tags, tagId, patch);

          return {
            ...model,
            tags: updatedTags,
          };
        });
      }
      queryClient.setQueryData(queryKey, updatedData);

      return { previousData };
    },

    onSuccess: async () => {
      await queryClient.cancelQueries({ queryKey });
      queryClient.refetchQueries({ queryKey });
    },

    onError: (error, _, context) => {
      console.log(error);
      errorToast('Failed to update tag');
      queryClient.setQueryData(queryKey, context?.previousData);
    },
  });

  const extractTags = useMutation({
    mutationFn: async ({ model, integrations, instructions, timeframe, seed, deepdive }: ExportTagProps) => {
      const res = await orgFetch<string>(`/api/org/facets/tagmodels/import/new?facetId=${model.id}`, {
        method: 'post',
        body: JSON.stringify({
          integrations,
          instructions,
          seed,
          deepdive,
          beforeTs: new Date().getTime() / 1000,
          afterTs: new Date().getTime() / 1000 - timeframe * 24 * 60 * 60,
          facetName: model.displayName || model.id,
          facetId: model.id,
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(`Unable to export tag: ${JSON.stringify(data)}`);
      }
      return data;
    },
  });

  return { updateTag, newTag, deleteTag, extractTags };
}
